import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type TicketChannel = "whatsapp" | "email" | "phone" | "web";
export type TicketPriority = "low" | "normal" | "high" | "urgent";
export type TicketStatus = "open" | "pending" | "resolved" | "closed";

export interface SupportTicket {
	id: string;
	number: string;
	subject: string;
	customerLabel: string | null;
	customerId: string | null;
	channel: TicketChannel;
	priority: TicketPriority;
	status: TicketStatus;
	assigneeId: string | null;
	assigneeLabel: string | null;
	slaDueAt: string | null;
	body: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface SupportStats {
	total: number;
	open: number;
	pending: number;
	urgent: number;
	resolved: number;
	unassigned: number;
	slaBreached: number;
}

export interface TicketFilter {
	status?: TicketStatus;
	priority?: TicketPriority;
	channel?: TicketChannel;
	q?: string;
}

export interface CreateTicketInput {
	subject: string;
	customerLabel?: string;
	channel?: TicketChannel;
	priority?: TicketPriority;
	body?: string;
}

export interface UpdateTicketInput {
	status?: TicketStatus;
	priority?: TicketPriority;
	assigneeId?: string | null;
	assigneeLabel?: string | null;
}

const toQs = (f: Record<string, unknown>): string => {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(f)) {
		if (v === undefined || v === null || v === "") continue;
		usp.set(k, String(v));
	}
	const qs = usp.toString();
	return qs ? `?${qs}` : "";
};

export const supportApi = {
	list: (filter: TicketFilter = {}) =>
		unwrap<{ items: SupportTicket[]; total: number }>(
			api.get<ApiEnvelope<{ items: SupportTicket[]; total: number }>>(
				`/admin/support/tickets${toQs(filter as Record<string, unknown>)}`,
			),
		),
	stats: () =>
		unwrap<SupportStats>(
			api.get<ApiEnvelope<SupportStats>>("/admin/support/stats"),
		),
	get: (id: string) =>
		unwrap<SupportTicket>(
			api.get<ApiEnvelope<SupportTicket>>(`/admin/support/tickets/${id}`),
		),
	create: (input: CreateTicketInput) =>
		unwrap<SupportTicket>(
			api.post<ApiEnvelope<SupportTicket>>("/admin/support/tickets", input),
		),
	update: (id: string, input: UpdateTicketInput) =>
		unwrap<SupportTicket>(
			api.patch<ApiEnvelope<SupportTicket>>(
				`/admin/support/tickets/${id}`,
				input,
			),
		),
};
